import React from 'react';
import styled, { css } from 'styled-components';
import { Box, Divider, Paragraph } from '@99designs/emu';
import { colors, spaces, typography } from '@99designs/emu/settings';
import { CurrencyCode } from '@99designs/i18n';
import { FlexLayout, FlexLayoutItem } from '@99designs/spa-common';
import { Price } from '@99designs/spa-common';
import List from '../../List';

const RadioWrapper = styled.div`
    display: inline-block;
`;

const Mark = styled.span`
    content: '';
    display: inline-block;
    position: relative;
    border: 1px solid ${colors.grey700};
    width: ${spaces['1.5bu']};
    height: ${spaces['1.5bu']};
    left: 0;
    top: 0;
    border-radius: 50%;
    margin-right: ${spaces['0.25bu']};
    &::after {
        content: '';
        display: block;
        width: 0;
        height: 0;
        border-radius: 50%;
        background-color: ${colors.forrestLyf};
        opacity: 0;
        left: 50%;
        top: 50%;
        position: absolute;
        transition: all 110ms;
    }
`;

const RadioInput = styled.input<{ checked: boolean }>`
    position: absolute;
    visibility: hidden;
    display: none;
    ${({ checked }) =>
        checked
            ? css`
                  & + ${Mark} {
                      &::after {
                          width: ${spaces['0.75bu']};
                          height: 9.5px;
                          opacity: 1;
                          left: ${spaces['0.25bu']};
                          top: ${spaces['0.25bu']};
                          vertical-align: middle;
                          text-align: center;
                      }
                  }
              `
            : ''}
`;

const Label = styled.label`
    display: flex;
    cursor: pointer;
    padding: ${spaces['0.25bu']} ${spaces['0.75bu']} ${spaces['0.25bu']} 0;
    font-weight: ${typography.fontWeights.bold};
    font-size: ${typography.typeScale.size3}px;
    font-family: ${typography.fontFamilies.heading};
    position: relative;
`;

const ItemSubtext = styled.div`
    margin-left: ${spaces['1.5bu']};
    color: ${colors.grey700};
    font-size: ${typography.typeScale.size2}px;
    margin-bottom: ${spaces['1.5bu']};
    margin-top: -${spaces['0.75bu']};
    margin-right: ${spaces['8bu']};
`;

const ItemPrice = styled.span`
    margin-right: 0;
    margin-top: ${spaces['1.5bu']};
    font-size: ${typography.typeScale.size3}px;
    font-family: ${typography.fontFamilies.heading};
`;

const Heading = styled.div`
    font-weight: ${typography.fontWeights.bold};
    font-size: ${typography.typeScale.size3}px;
    font-family: ${typography.fontFamilies.heading};
    margin-bottom: 0;
`;

export interface PriceRange {
    rangeLow: number;
    rangeHigh?: number;
    currency: string;
}

export interface RadionOption {
    name: string;
    value: string;
    label: string;
    subtext?: string;
    purchasePrice?: string | PriceRange;
}

interface RadioListGrid {
    selectedValue: string;
    label: string;
    onChange: (value: string) => void;
    options: RadionOption[];
    subtext: string;
}

function RadioListGrid({ label, options, selectedValue, subtext, onChange }: RadioListGrid) {
    const renderPurchasePrice = (purchasePrice?: string | PriceRange) => {
        if (purchasePrice === undefined) {
            return '';
        }

        if (typeof purchasePrice === 'string') {
            return purchasePrice;
        }

        const { rangeLow, rangeHigh, currency } = purchasePrice;

        if (rangeLow > 0 && !rangeHigh) {
            return (
                <Paragraph>
                    from <Price amount={rangeLow} currency={currency as CurrencyCode} />
                </Paragraph>
            );
        }

        if (rangeLow === 0) {
            return <Price amount={rangeHigh ?? 0} currency={currency as CurrencyCode} />;
        }

        return (
            <>
                <Price amount={rangeLow} currency={currency as CurrencyCode} />
                {' – '}
                <Price amount={rangeHigh ?? 0} currency={currency as CurrencyCode} />
            </>
        );
    };

    return (
        <List spacing="small">
            <Heading>{label}</Heading>
            <Box marginTop={'0.75bu'} marginBottom={'1.5bu'}>
                <Paragraph>{subtext}</Paragraph>
            </Box>
            <Divider />
            {options.map(({ value, purchasePrice, subtext, label }, index) => (
                <React.Fragment key={index}>
                    <Box marginTop="1bu" marginBottom="1bu">
                        <FlexLayout>
                            <FlexLayoutItem type="grow" key={value}>
                                <RadioWrapper>
                                    <Label>
                                        <RadioInput
                                            type="radio"
                                            value={value}
                                            // eslint-disable-next-line eqeqeq
                                            checked={value == selectedValue}
                                            onChange={(e) => onChange(e.target.value)}
                                            data-testid={`radio-${value}`}
                                        />
                                        <Mark />
                                        {label}
                                    </Label>
                                </RadioWrapper>
                            </FlexLayoutItem>
                            <FlexLayoutItem>
                                <ItemPrice>{renderPurchasePrice(purchasePrice)}</ItemPrice>
                            </FlexLayoutItem>
                        </FlexLayout>
                    </Box>
                    {subtext && <ItemSubtext>{subtext}</ItemSubtext>}
                    {/* eslint-disable-next-line eqeqeq */}
                    {index != options.length - 1 && <Divider />}
                </React.Fragment>
            ))}
        </List>
    );
}

export default RadioListGrid;
